import { AxiosError } from 'axios';

const DUPLICATE_NAME_MARKER = 'already exists';
const DUPLICATE_NAME_TYPE = 'DUPLICATE_NAME';

/**
 * Extracts a human readable error message from a failed page-template
 * create/update request. The endpoint returns either a plain JSON string
 * (e.g. "A template with the same name already exists for this page type") or a
 * hash with a `message` field, so we handle both shapes.
 */
export const getTemplateApiErrorMessage = (
  error: unknown,
): string | undefined => {
  const responseData = (error as AxiosError)?.response?.data as unknown;

  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData.trim();
  }

  if (responseData && typeof responseData === 'object') {
    const message = (responseData as Record<string, unknown>).message;

    if (typeof message === 'string' && message.trim()) {
      return message.trim();
    }
  }

  return undefined;
};

/**
 * Returns true when a page-template save failed because a template with the
 * same name already exists for the page type.
 *
 * Matches when status is 400 AND either the response declares
 * `type === 'DUPLICATE_NAME'` or its message contains the duplicate-name marker.
 */
export const isDuplicateTemplateNameError = (error: unknown): boolean => {
  const status = (error as AxiosError)?.response?.status;

  if (status !== 400) {
    return false;
  }

  const responseData = (error as AxiosError)?.response?.data as unknown;

  if (
    responseData &&
    typeof responseData === 'object' &&
    (responseData as Record<string, unknown>).type === DUPLICATE_NAME_TYPE
  ) {
    return true;
  }

  const message = getTemplateApiErrorMessage(error);

  return (
    typeof message === 'string' &&
    message.toLowerCase().includes(DUPLICATE_NAME_MARKER)
  );
};
